import React, { MouseEventHandler } from 'react';
import styled from 'styled-components';
import { useCollapse } from 'react-collapsed';
import { getQueryTypePagePath } from '../../utils';
import {
  IAnalysisVisitFieldsFragment,
  IUserSettingUpdateMutation,
  IUserSettingUpdateMutationVariables,
  UserSettingKind,
} from '../../utils/apollo/graphqlSchema';
import { LoadingSpinner } from '../Loaders';
import { IconWrapper, SvgIcon } from '../Icon';
import { useAppDispatch } from '../../redux/hooks';
import { useMutation } from '@apollo/client';
import { USER_SETTING_UPDATE } from '../../utils/apollo/userSettings';
import { setExecutionHistorySidebarCollapsed } from '../../redux/slices/userSettings';
import { useUserSettingsSelector } from '../../redux/selectorHooks';
import ReportHistoryLink from './ReportHistoryLink';
import { theme } from '../../styles/theme';
import ScrollableWrapper from '../ScrollableWrapper/ScrollableWrapper';

interface IProps {
  heading: string;
  visits: IAnalysisVisitFieldsFragment[];
  onLinkClick?: MouseEventHandler<HTMLAnchorElement>;
  loading?: boolean;
  selectedAnalysisId: string | undefined;
}

const Container = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const HeaderWrapper = styled.div`
  padding-right: 12px;
  padding-left: 12px;
`;

const Header = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 3px;
  padding-bottom: 3px;
  border-bottom: 1px solid ${theme.colors.border};
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  color: ${theme.colors.secondary800};
  cursor: pointer;
`;

const LinksWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  padding: 8px 12px 8px 12px;
`;

const ContentWrapper = styled.div`
  flex-grow: 1;
`;

const ReportHistory = ({
  heading,
  visits,
  onLinkClick,
  loading,
  selectedAnalysisId,
}: IProps) => {
  const dispatch = useAppDispatch();
  const isExecutionHistorySidebarExpanded = useUserSettingsSelector(
    'isExecutionHistorySidebarExpanded',
  );

  const { getCollapseProps, getToggleProps } = useCollapse({
    isExpanded: isExecutionHistorySidebarExpanded,
    hasDisabledAnimation: true,
  });

  const [saveSettings] = useMutation<
    IUserSettingUpdateMutation,
    IUserSettingUpdateMutationVariables
  >(USER_SETTING_UPDATE);

  const toggleExecutionHistorySidebar = async () => {
    const newState = !isExecutionHistorySidebarExpanded;
    await saveSettings({
      variables: {
        input: {
          userSettings: [
            {
              kind: UserSettingKind.ExecutionHistoryExpanded,
              value: Boolean(newState).toString(),
            },
          ],
        },
      },
    });
    dispatch(setExecutionHistorySidebarCollapsed(newState));
  };

  return (
    <Container>
      <HeaderWrapper>
        <Header {...getToggleProps({ onClick: toggleExecutionHistorySidebar })}>
          <span>{heading}</span>
          <div>
            <LoadingSpinner active={loading} inline size="tiny" />
            <IconWrapper>
              <SvgIcon
                name={
                  isExecutionHistorySidebarExpanded
                    ? 'caret-down-fill'
                    : 'caret-right-fill'
                }
                width={12}
                height={12}
              />
            </IconWrapper>
          </div>
        </Header>
      </HeaderWrapper>
      <ScrollableWrapper>
        <ContentWrapper {...getCollapseProps()}>
          <LinksWrapper>
            {visits.map(({ id, queryType, display, status, createdAt }) => (
              <ReportHistoryLink
                key={`report-nav-history-${id}`}
                id={id}
                queryType={queryType}
                display={display}
                status={status}
                createdAt={createdAt}
                url={getQueryTypePagePath(queryType, id)}
                onClick={onLinkClick}
                isSelected={id === selectedAnalysisId}
              />
            ))}
          </LinksWrapper>
        </ContentWrapper>
      </ScrollableWrapper>
    </Container>
  );
};

export default ReportHistory;
